import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, TrendingUp, Home, ArrowRight, Phone, Calculator, 
  Users, Award, MapPin, Clock, CheckCircle, Star, Building2,
  Handshake, FileText, HeartHandshake, Sparkles, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Testimonials from '../components/Testimonials';
import { useLanguage } from '../context/LanguageContext';

const HomePage = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-white">
      {/* ========== Hero Section ========== */}
      <section className="relative min-h-[90vh] flex items-center justify-center text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxBc2lhbiUyMGZhbWlseSUyMGZpbmFuY2lhbCUyMGFkdmlzb3IlMjBtZWV0aW5nJTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc3MDMzOTcwNXww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#003D5C]/90 via-[#0077B6]/80 to-[#0077B6]" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/20 backdrop-blur-sm mb-8">
            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-sm font-medium text-white">{t('hero.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight">
            {t('hero.title')}{' '}
            <span className="text-[#D4AF37]">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-lg md:text-xl mb-10 text-gray-100 max-w-3xl mx-auto leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8 py-6 shadow-lg">
              <Link to="/contact">{t('hero.bookConsultation')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
              <a href="tel:18772399060">
                <Phone className="mr-2 h-5 w-5" />
                1-877-239-9060
              </a>
            </Button>
            <Button asChild size="lg" className="bg-white hover:bg-gray-100 text-[#0077B6] font-bold text-lg px-8 py-6">
              <Link to="/insurance#quote">
                <Calculator className="mr-2 h-5 w-5" />
                {t('hero.getQuote')}
              </Link>
            </Button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ========== SECTION 1: Our Services (White Background) ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37]">{t('services.label')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mt-3 mb-4">{t('services.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Insurance Card */}
            <Card className="bg-gradient-to-br from-[#F0F7FF] to-white border-2 border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-xl group overflow-hidden">
              <CardContent className="p-8 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0077B6]/5 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 rounded-2xl bg-[#0077B6] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-[#0077B6] transition-colors">{t('services.insurance.title')}</h3>
                <p className="text-gray-600 text-base mb-4 leading-relaxed">
                  {t('services.insurance.description')}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.insurance.lifeInsurance')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.insurance.criticalIllness')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.insurance.visitorsCanada')}
                  </li>
                </ul>
                <Button asChild variant="link" className="text-[#0077B6] p-0 font-bold text-base hover:text-[#005A8C]">
                  <Link to="/insurance">
                    {t('services.insurance.explore')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Investments Card */}
            <Card className="bg-gradient-to-br from-[#FDF8E8] to-white border-2 border-yellow-100 hover:border-[#D4AF37] transition-all hover:shadow-xl group overflow-hidden">
              <CardContent className="p-8 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/5 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <TrendingUp className="h-8 w-8 text-[#003D5C]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-[#D4AF37] transition-colors">{t('services.investment.title')}</h3>
                <p className="text-gray-600 text-base mb-4 leading-relaxed">
                  {t('services.investment.description')}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#0077B6] mr-2" />{t('services.investment.rrspTfsa')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#0077B6] mr-2" />{t('services.investment.respFhsa')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#0077B6] mr-2" />{t('services.investment.segregatedFunds')}
                  </li>
                </ul>
                <Button asChild variant="link" className="text-[#D4AF37] p-0 font-bold text-base hover:text-[#B8962E]">
                  <Link to="/investment">
                    {t('services.investment.explore')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Mortgage Card */}
            <Card className="bg-gradient-to-br from-[#F0F7FF] to-white border-2 border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-xl group overflow-hidden">
              <CardContent className="p-8 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0077B6]/5 rounded-full -mr-16 -mt-16"></div>
                <div className="w-16 h-16 rounded-2xl bg-[#0077B6] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <Home className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-[#0077B6] transition-colors">{t('services.mortgage.title')}</h3>
                <p className="text-gray-600 text-base mb-4 leading-relaxed">
                  {t('services.mortgage.description')}
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.mortgage.purchaseRefinance')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.mortgage.newToCanada')}
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />{t('services.mortgage.selfEmployed')}
                  </li>
                </ul>
                <Button asChild variant="link" className="text-[#0077B6] p-0 font-bold text-base hover:text-[#005A8C]">
                  <Link to="/mortgage">
                    {t('services.mortgage.explore')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Why Choose Us (Dark Background) ========== */}
      <section className="py-16 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37]">{t('whyChooseUs.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">{t('whyChooseUs.title')}</h2>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto">
              {t('whyChooseUs.subtitle')}
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="text-center p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-all">
              <div className="bg-[#D4AF37] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-[#003D5C]" />
              </div>
              <div className="text-3xl font-bold text-[#D4AF37] mb-2">21+</div>
              <h3 className="font-semibold text-lg text-white mb-1">{t('whyChooseUs.stats.advisors')}</h3>
              <p className="text-sm text-blue-200">{t('whyChooseUs.stats.advisorsDesc')}</p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-all">
              <div className="bg-[#D4AF37] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-[#003D5C]" />
              </div>
              <div className="text-3xl font-bold text-[#D4AF37] mb-2">4</div>
              <h3 className="font-semibold text-lg text-white mb-1">{t('whyChooseUs.stats.provinces')}</h3>
              <p className="text-sm text-blue-200">{t('whyChooseUs.stats.provincesDesc')}</p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-all">
              <div className="bg-[#D4AF37] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Handshake className="h-8 w-8 text-[#003D5C]" />
              </div>
              <div className="text-3xl font-bold text-[#D4AF37] mb-2">50+</div>
              <h3 className="font-semibold text-lg text-white mb-1">{t('whyChooseUs.stats.lenders')}</h3>
              <p className="text-sm text-blue-200">{t('whyChooseUs.stats.lendersDesc')}</p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-all">
              <div className="bg-[#D4AF37] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-[#003D5C]" />
              </div>
              <div className="text-3xl font-bold text-[#D4AF37] mb-2">15+</div>
              <h3 className="font-semibold text-lg text-white mb-1">{t('whyChooseUs.stats.carriers')}</h3>
              <p className="text-sm text-blue-200">{t('whyChooseUs.stats.carriersDesc')}</p>
            </div>
          </div>

          {/* Core Values */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h3 className="text-xl font-bold text-white text-center mb-6">{t('whyChooseUs.coreValues.title')}</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <HeartHandshake className="h-5 w-5 text-[#003D5C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{t('whyChooseUs.coreValues.clientFirst.title')}</h4>
                  <p className="text-sm text-blue-200">{t('whyChooseUs.coreValues.clientFirst.description')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-[#003D5C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{t('whyChooseUs.coreValues.transparent.title')}</h4>
                  <p className="text-sm text-blue-200">{t('whyChooseUs.coreValues.transparent.description')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[#003D5C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{t('whyChooseUs.coreValues.longTerm.title')}</h4>
                  <p className="text-sm text-blue-200">{t('whyChooseUs.coreValues.longTerm.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: Who We Serve (White Background) ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#0077B6]">Who We Help</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3 mb-4">Solutions for Every Stage of Life</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We serve Canadians at every financial milestone
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#0077B6] flex items-center justify-center flex-shrink-0">
                <Home className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">First-Time Home Buyers</h3>
                <p className="text-gray-600 text-sm">Navigate your first purchase with expert guidance on mortgages, down payments, and FHSA.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-[#003D5C]" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">Growing Families</h3>
                <p className="text-gray-600 text-sm">Protect your loved ones with life insurance and start saving for education with RESPs.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#0077B6] flex items-center justify-center flex-shrink-0">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">Business Owners</h3>
                <p className="text-gray-600 text-sm">Group benefits, key person insurance, and corporate investment strategies.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <Globe className="h-6 w-6 text-[#003D5C]" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">New to Canada</h3>
                <p className="text-gray-600 text-sm">Specialized mortgage programs and visitor insurance for newcomers and immigrants.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#0077B6] flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">Pre-Retirees</h3>
                <p className="text-gray-600 text-sm">Maximize your retirement savings with RRSPs, pension transfers, and income planning.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 rounded-xl bg-[#F0F7FF] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <HeartHandshake className="h-6 w-6 text-[#003D5C]" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-1">Retirees</h3>
                <p className="text-gray-600 text-sm">CHIP reverse mortgages, annuities, and estate planning for your golden years.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: Testimonials ========== */}
      <Testimonials />

      {/* ========== SECTION 5: Our Process (Dark Background) ========== */}
      <section className="py-16 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37]">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">Our Simple Process</h2>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto">
              Getting started is easy - here's how we work with you
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center relative">
              <div className="bg-[#D4AF37] text-[#003D5C] rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
              <h3 className="font-bold text-lg text-white mb-2">Free Consultation</h3>
              <p className="text-blue-200 text-sm">Schedule a no-obligation call to discuss your financial goals and needs.</p>
              <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-0.5 bg-[#D4AF37]/30"></div>
            </div>
            
            <div className="text-center relative">
              <div className="bg-[#D4AF37] text-[#003D5C] rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
              <h3 className="font-bold text-lg text-white mb-2">Personalized Plan</h3>
              <p className="text-blue-200 text-sm">We analyze your situation and create a customized financial strategy.</p>
              <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-0.5 bg-[#D4AF37]/30"></div>
            </div>
            
            <div className="text-center relative">
              <div className="bg-[#D4AF37] text-[#003D5C] rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
              <h3 className="font-bold text-lg text-white mb-2">Implementation</h3>
              <p className="text-blue-200 text-sm">We help you execute the plan with the right products and solutions.</p>
              <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-0.5 bg-[#D4AF37]/30"></div>
            </div>
            
            <div className="text-center">
              <div className="bg-[#D4AF37] text-[#003D5C] rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">4</div>
              <h3 className="font-bold text-lg text-white mb-2">Ongoing Support</h3>
              <p className="text-blue-200 text-sm">Regular reviews and adjustments to keep your plan on track.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 6: Featured Resources (White Background) ========== */}
      <section className="py-16 bg-[#F0F7FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#0077B6]">Resources</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3 mb-4">Helpful Tools & Resources</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#0077B6]/10 flex items-center justify-center mb-4">
                  <Calculator className="h-6 w-6 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Insurance Quote Calculator</h3>
                <p className="text-gray-600 text-sm mb-4">Get instant estimates for life and critical illness insurance.</p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/insurance#quote">Get a Quote</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="bg-white hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Financial Resources</h3>
                <p className="text-gray-600 text-sm mb-4">Access calculators, guides, and educational content.</p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/resources">Browse Resources</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="bg-white hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#0077B6]/10 flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Blog & Insights</h3>
                <p className="text-gray-600 text-sm mb-4">Read our latest articles on insurance, mortgages, and investments.</p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/blog">Read Blog</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 7: Contact CTA (Blue Background) ========== */}
      <section className="py-16 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Ready to Take the Next Step?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Book a free consultation and get a clear path to your financial goals. No pressure, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8 py-6 shadow-lg">
              <Link to="/contact">Book Free Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
              <Link to="/team">Meet Our Team</Link>
            </Button>
          </div>
          
          {/* Contact Info */}
          <div className="mt-10 pt-8 border-t border-white/20">
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
              <a href="tel:18772399060" className="flex items-center text-white hover:text-[#D4AF37] transition-colors">
                <Phone className="h-5 w-5 mr-2" />
                <span className="font-medium">1-877-239-9060</span>
              </a>
              <span className="hidden md:block text-white/30">|</span>
              <a href="mailto:hello@utmostfinancial.ca" className="flex items-center text-white hover:text-[#D4AF37] transition-colors">
                <span className="font-medium">hello@utmostfinancial.ca</span>
              </a>
              <span className="hidden md:block text-white/30">|</span>
              <span className="flex items-center text-white">
                <MapPin className="h-5 w-5 mr-2" />
                <span>Winnipeg & Edmonton</span>
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
